// Spec 48 ai-ux — refusal_panel.
//
// "A distinct surface — not a regular assistant bubble — explaining why
// a request was refused. Names the specific blocker (permission,
// policy, disabled capability) and offers at least one next step."
// (spec 48 line 879)
//
// Composes naturally with @novemai/ai-platform's AIRefusalEnvelope:
// caller takes the envelope, picks an alternative-action set, and
// passes them as props.
//
// a11y per spec 48:
//   - Panel is role="region" with aria-label="AI refusal"
//   - Title is <h3> inside the panel for document structure
//   - Every alternative action is a real button or link with focus ring
//   - Icon aria-hidden; reason carries the meaning in text

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export type RefusalKindCategory = "permission" | "policy" | "capability" | "other";

export interface RefusalAlternativeAction {
  readonly id: string;
  readonly label: string;
  /** Either onClick (button) or href (link) — caller picks affordance. */
  readonly onClick?: () => void;
  readonly href?: string;
}

export interface RefusalPanelProps extends HTMLAttributes<HTMLElement> {
  readonly kind: RefusalKindCategory;
  /** Visible title — defaults vary by kind. */
  readonly title?: string;
  /** Named blocker, e.g., "permission: comp.read" or "capability: ai_hris_bulk_update (disabled)". */
  readonly blockerName?: string;
  /** 1–2 sentence human explanation. */
  readonly reason: string;
  readonly alternatives?: readonly RefusalAlternativeAction[];
  readonly icon?: ReactNode;
  /** Override panel aria-label. */
  readonly panelAriaLabel?: string;
  /** Override the alternatives group aria-label. */
  readonly alternativesAriaLabel?: string;
}

const DEFAULT_TITLE: Readonly<Record<RefusalKindCategory, string>> = {
  permission: "I can't see this",
  policy: "Blocked by policy",
  capability: "This is turned off",
  other: "I can't do this",
};

export const RefusalPanel = forwardRef<HTMLElement, RefusalPanelProps>(
  (
    {
      kind,
      title,
      blockerName,
      reason,
      alternatives,
      icon,
      panelAriaLabel = "AI refusal",
      alternativesAriaLabel = "Next steps",
      className,
      ...rest
    },
    ref,
  ) => {
    const heading = title ?? DEFAULT_TITLE[kind];
    return (
      <section
        ref={ref}
        // <section> with an accessible name (aria-label) is already
        // exposed as a region landmark by the AT — explicit role would be
        // redundant per WAI-ARIA + jsx-a11y/no-redundant-roles.
        aria-label={panelAriaLabel}
        data-component="refusal-panel"
        data-kind={kind}
        className={cx("nai-refusal-panel", `nai-refusal-panel--${kind}`, className)}
        {...rest as HTMLAttributes<HTMLElement>}
      >
        <div className="nai-refusal-panel-head">
          {icon !== undefined && (
            <span aria-hidden="true" data-component="refusal-panel-icon" className="nai-refusal-panel-icon">
              {icon}
            </span>
          )}
          <h3 data-component="refusal-panel-title" className="nai-refusal-panel-title">
            {heading}
          </h3>
        </div>
        <p data-component="refusal-panel-reason" className="nai-refusal-panel-reason">
          {reason}
        </p>
        {blockerName !== undefined && (
          <p data-component="refusal-panel-blocker" className="nai-refusal-panel-blocker">
            {blockerName}
          </p>
        )}
        {alternatives !== undefined && alternatives.length > 0 && (
          <div
            data-component="refusal-panel-alternatives"
            className="nai-refusal-panel-alternatives"
            role="group"
            aria-label={alternativesAriaLabel}
          >
            {alternatives.map((alt) =>
              alt.href !== undefined ? (
                <a
                  key={alt.id}
                  href={alt.href}
                  data-component="refusal-panel-alternative"
                  className="nai-refusal-panel-alternative"
                >
                  {alt.label}
                </a>
              ) : (
                <button
                  key={alt.id}
                  type="button"
                  data-component="refusal-panel-alternative"
                  className="nai-refusal-panel-alternative"
                  onClick={alt.onClick}
                >
                  {alt.label}
                </button>
              ),
            )}
          </div>
        )}
      </section>
    );
  },
);
RefusalPanel.displayName = "RefusalPanel";
